import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  readCookieConsent,
  saveCookieConsent,
  type CookieConsentPreferences
} from '../../services/cookieConsent';

const localePrefixFromPath = (pathname: string) => pathname.match(/^\/(en|it|es|fr|de)(?=\/|$)/)?.[0] || '';

const withLocalePrefix = (path: string, pathname: string) => {
  const prefix = localePrefixFromPath(pathname);
  if (!prefix) {
    return path;
  }
  return path === '/' ? prefix : `${prefix}${path}`;
};

const CookieBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [consent, setConsent] = useState<CookieConsentPreferences | null>(() => readCookieConsent());
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const syncConsent = (event: Event) => {
      setConsent((event as CustomEvent<CookieConsentPreferences>).detail || readCookieConsent());
    };

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
  }, []);

  if (consent) {
    return null;
  }

  const save = (allowAnalytics: boolean) => {
    setConsent(saveCookieConsent({ analytics: allowAnalytics }, 'banner'));
  };

  return (
    <aside
      aria-label={t('legal.cookies.banner.ariaLabel')}
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-5xl rounded-[20px] border border-[#90e0ef] bg-white p-4 text-[#03045e] shadow-2xl sm:bottom-5 sm:p-5"
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_300px] lg:items-end">
        <div className="space-y-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-700">{t('legal.cookies.banner.eyebrow')}</p>
            <h2 className="mt-1 font-display text-xl font-semibold">{t('legal.cookies.banner.title')}</h2>
            <p className="mt-2 text-sm leading-6 text-[#3a5580]">{t('legal.cookies.banner.description')}</p>
          </div>

          {isCustomizing ? (
            <div className="rounded-2xl border border-[#caf0f8] bg-[#f2fcff] p-3">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-[#90e0ef] text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="block font-semibold">{t('legal.cookies.banner.analyticsTitle')}</span>
                  <span className="block leading-6 text-[#3a5580]">{t('legal.cookies.banner.analyticsDescription')}</span>
                </span>
              </label>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3 text-xs font-semibold">
            <Link to={withLocalePrefix('/privacy', location.pathname)} className="text-primary-700 underline underline-offset-4">
              {t('footer.links.privacy')}
            </Link>
            <Link to={withLocalePrefix('/cookies', location.pathname)} className="text-primary-700 underline underline-offset-4">
              {t('footer.links.cookies')}
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => save(true)}
            className="rounded-full bg-[#0077b6] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#0077b6]/25 transition hover:bg-[#005f99] focus:outline-none focus:ring-4 focus:ring-[#90e0ef]"
          >
            {t('legal.cookies.banner.acceptPrimary')}
          </button>
          <button
            type="button"
            onClick={() => save(false)}
            className="rounded-full border border-[#90e0ef] bg-white px-5 py-3 text-sm font-semibold text-[#03045e] transition hover:bg-[#e9fbff]"
          >
            {t('legal.cookies.banner.necessaryOnly')}
          </button>
          {isCustomizing ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <button
                type="button"
                onClick={() => save(analytics)}
                className="rounded-full border border-[#90e0ef] bg-[#e9fbff] px-5 py-3 text-sm font-semibold text-[#03045e] transition hover:bg-[#dff7ff]"
              >
                {t('legal.cookies.banner.save')}
              </button>
              <button
                type="button"
                onClick={() => save(false)}
                className="rounded-full px-5 py-3 text-sm font-semibold text-[#03045e] transition hover:bg-[#e9fbff]"
              >
                {t('legal.cookies.banner.rejectOptional')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsCustomizing(true)}
              className="rounded-full px-5 py-3 text-sm font-semibold text-[#03045e] transition hover:bg-[#e9fbff]"
            >
              {t('legal.cookies.banner.customize')}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default CookieBanner;
